import React, { useState, useEffect } from 'react';
import { GetRequest, PostRequest, DeleteRequest } from 'components/common/index.js';
import { getDataSourceIcon, DataSourceConnectButtons } from './DataSourceIcons';
import GoogleSheetsPicker from './form_fields/GoogleSheetsPicker';
import OneDrivePicker from './form_fields/OneDrivePicker';

const DataSourcesSettings = () => {
  const [dataSources, setDataSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPickerForDataSource, setShowPickerForDataSource] = useState(null);

  const fetchDataSources = () => {
    setLoading(true);
    GetRequest({
      url: '/quote-builder/data-sources',
      callback: (response) => {
        setDataSources(response.data_sources || []);
        setLoading(false);
      },
      error: () => {
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    fetchDataSources();

    // Listen for OAuth popup completion
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data && (event.data.type === 'google_sheets_oauth_complete' || event.data.type === 'microsoft_excel_oauth_complete' || event.data.type === 'airtable_oauth_complete')) {
        const oauthType = event.data.type;
        const result = event.data.result;

        // Fetch updated data sources
        GetRequest({
          url: '/quote-builder/data-sources',
          callback: (response) => {
            setDataSources(response.data_sources || []);
            setLoading(false);

            // Auto-open picker for newly connected data source
            if (result && result.success && result.dataSourceId) {
              const newDataSource = (response.data_sources || []).find(ds => ds.id == result.dataSourceId);
              if (newDataSource) {
                // Google Sheets - check if no spreadsheets picked yet
                if (oauthType === 'google_sheets_oauth_complete' && newDataSource.source_type === 'google_sheets') {
                  if (!newDataSource.picked_spreadsheets || newDataSource.picked_spreadsheets.length === 0) {
                    setShowPickerForDataSource(previousValue => {
                      if (previousValue) return previousValue;
                      return newDataSource;
                    });
                  }
                }
                // Microsoft Excel - check if no workbooks picked yet
                if (oauthType === 'microsoft_excel_oauth_complete' && newDataSource.source_type === 'microsoft_excel') {
                  if (!newDataSource.picked_workbooks || newDataSource.picked_workbooks.length === 0) {
                    setShowPickerForDataSource(previousValue => {
                      if (previousValue) return previousValue;
                      return newDataSource;
                    });
                  }
                }
              }
            }
          }
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleConnectGoogleSheets = () => {
    openOAuthPopup('/quote-builder/data-sources/google-sheets/authorize', 'google_sheets_auth');
  };

  const handleConnectMicrosoftExcel = () => {
    openOAuthPopup('/quote-builder/data-sources/microsoft-excel/authorize', 'microsoft_excel_auth');
  };

  const handleConnectAirtable = () => {
    openOAuthPopup('/quote-builder/data-sources/airtable/authorize', 'airtable_auth');
  };

  const openOAuthPopup = (url, name) => {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    window.open(
      url,
      name,
      `width=${width},height=${height},left=${left},top=${top}`
    );
    // Note: We don't poll for popup close - the OAuth completion is handled
    // via postMessage in the useEffect handleMessage listener
  };

  const handleUnlink = (dataSource) => {
    if (!confirm(t('quote_builder.unlink_data_source_confirm', { name: dataSource.name }))) {
      return;
    }

    PostRequest({
      url: `/quote-builder/data-sources/${dataSource.id}/unlink`,
      callback: () => {
        fetchDataSources();
      }
    });
  };

  const handleRemoveSpreadsheet = (dataSource, spreadsheetId, spreadsheetName) => {
    if (!confirm(t('quote_builder.remove_spreadsheet_confirm', { name: spreadsheetName }))) {
      return;
    }

    DeleteRequest({
      url: `/quote-builder/data-sources/${dataSource.id}/spreadsheets/${encodeURIComponent(spreadsheetId)}`,
      callback: () => {
        fetchDataSources();
      }
    });
  };

  const handleRemoveWorkbook = (dataSource, workbookId, workbookName) => {
    if (!confirm(t('quote_builder.remove_workbook_confirm', { name: workbookName }))) {
      return;
    }

    DeleteRequest({
      url: `/quote-builder/data-sources/${dataSource.id}/workbooks/${encodeURIComponent(workbookId)}`,
      callback: () => {
        fetchDataSources();
      }
    });
  };

  const handleAddSpreadsheet = (dataSource) => {
    setShowPickerForDataSource(dataSource);
  };

  const handleSpreadsheetsPicked = () => {
    setShowPickerForDataSource(null);
    fetchDataSources();
  };

  const handlePickerCancel = () => {
    setShowPickerForDataSource(null);
  };

  const getSourceTypeIcon = (sourceType) => {
    return getDataSourceIcon(sourceType, { height: '20px', marginRight: '8px' });
  };

  const getSourceTypeName = (sourceType) => {
    if (sourceType === 'google_sheets') {
      return t('quote_builder.google_sheets');
    } else if (sourceType === 'microsoft_excel') {
      return t('quote_builder.microsoft_excel');
    } else if (sourceType === 'airtable') {
      return t('quote_builder.airtable');
    }
    return sourceType;
  };

  const getSyncStatusBadge = (status) => {
    const statusClasses = {
      'active': 'label-success',
      'pending': 'label-warning',
      'error': 'label-danger',
      'disconnected': 'label-default'
    };
    const statusLabels = {
      'active': t('quote_builder.sync_status_active'),
      'pending': t('quote_builder.sync_status_pending'),
      'error': t('quote_builder.sync_status_error'),
      'disconnected': t('quote_builder.sync_status_disconnected')
    };
    return <span className={`label ${statusClasses[status] || 'label-default'}`}>{statusLabels[status] || status}</span>;
  };

  const renderPickedFilesList = (dataSource) => {
    // Only show for Google Sheets and Microsoft Excel (not Airtable)
    if (dataSource.source_type !== 'google_sheets' && dataSource.source_type !== 'microsoft_excel') {
      return null;
    }

    const files = dataSource.source_type === 'google_sheets'
      ? (dataSource.picked_spreadsheets || [])
      : (dataSource.picked_workbooks || []);

    const addButtonLabel = dataSource.source_type === 'google_sheets'
      ? t('quote_builder.add_spreadsheet')
      : t('quote_builder.add_workbook');

    return (
      <div style={{ marginTop: '10px', marginLeft: '28px' }}>
        {files.length > 0 && (
          <ul style={{ marginTop: '0', marginBottom: '8px', paddingLeft: '0', listStyle: 'none' }}>
            {files.map((file) => (
              <li key={file.id} style={{ marginBottom: '6px', display: 'flex', alignItems: 'center' }}>
                <i className="fa fa-file-o" aria-hidden="true" style={{ marginRight: '8px', color: '#666' }}></i>
                <a href={file.url} target="_blank" rel="noopener noreferrer" style={{ color: '#337ab7' }}>
                  {file.name}
                </a>
                <span style={{ margin: '0 8px', color: '#999' }}>-</span>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (dataSource.source_type === 'google_sheets') {
                      handleRemoveSpreadsheet(dataSource, file.id, file.name);
                    } else {
                      handleRemoveWorkbook(dataSource, file.id, file.name);
                    }
                  }}
                  style={{ color: '#999', fontSize: '12px' }}
                >
                  <i className="fa fa-chain-broken" aria-hidden="true" style={{ marginRight: '4px' }}></i>
                  {t('quote_builder.remove_access')}
                </a>
              </li>
            ))}
          </ul>
        )}
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); handleAddSpreadsheet(dataSource); }}
          style={{ fontSize: '13px', color: '#5cb85c' }}
        >
          <i className="fa fa-plus" aria-hidden="true" style={{ marginRight: '5px' }}></i>
          {addButtonLabel}
        </a>
      </div>
    );
  };

  const dataSourceRow = (dataSource) => {
    return (
      <tr key={dataSource.id}>
        <td>
          <div>
            {getSourceTypeIcon(dataSource.source_type)}
            {dataSource.name}
          </div>
          {renderPickedFilesList(dataSource)}
        </td>
        <td>{getSourceTypeName(dataSource.source_type)}</td>
        <td>{getSyncStatusBadge(dataSource.sync_status)}</td>
        <td>
          <a href="#" onClick={(e) => { e.preventDefault(); handleUnlink(dataSource); }}>
            <i className="fa fa-chain-broken" aria-hidden="true"></i> {t('quote_builder.unlink')}
          </a>
        </td>
      </tr>
    );
  };

  const listOfDataSources = () => {
    return (
      <table className="table table-striped table-bordered-outside quote_builder_table">
        <tbody>
          <tr>
            <th colSpan="4">{t('quote_builder.my_data_sources')} ({dataSources.length})</th>
          </tr>
          <tr>
            <th>{t('quote_builder.name')}</th>
            <th>{t('quote_builder.source_type')}</th>
            <th>{t('quote_builder.status')}</th>
            <th></th>
          </tr>
          {dataSources.map(dataSourceRow)}
        </tbody>
      </table>
    );
  };

  if (loading) {
    return (
      <div style={{ marginLeft: '-20px' }}>
        <p><i className="fa fa-spinner fa-spin"></i> {t('quote_builder.loading')}</p>
      </div>
    );
  }

  return (
    <div style={{ marginLeft: '0px' }}>
      {showPickerForDataSource && showPickerForDataSource.source_type === 'google_sheets' && (
        <GoogleSheetsPicker
          dataSourceId={showPickerForDataSource.id}
          onSpreadsheetsPicked={handleSpreadsheetsPicked}
          onCancel={handlePickerCancel}
        />
      )}
      {showPickerForDataSource && showPickerForDataSource.source_type === 'microsoft_excel' && (
        <OneDrivePicker
          dataSourceId={showPickerForDataSource.id}
          onWorkbooksPicked={handleSpreadsheetsPicked}
          onCancel={handlePickerCancel}
        />
      )}
      {dataSources.length > 0 && listOfDataSources()}

       <div style={{ marginLeft: '-10px' }}>
      {dataSources.length === 0 && (
        <div style={{ marginTop: '10px', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
          <h4>{t('quote_builder.getting_started')}</h4>
          <p dangerouslySetInnerHTML={{ __html: t('quote_builder.data_sources_getting_started_text') }} />
        </div>
      )}
      <br />

        <h4> &nbsp; &nbsp; {t('quote_builder.add_new_data_source')}</h4>
        <div style={{ marginTop: '15px', marginLeft: '10px'}}>
          <DataSourceConnectButtons
            onConnectGoogleSheets={handleConnectGoogleSheets}
            onConnectMicrosoftExcel={handleConnectMicrosoftExcel}
            onConnectAirtable={handleConnectAirtable}
          />
        </div>
        <p style={{ marginTop: '15px' , marginLeft: '10px'}}>
          <a href="https://www.diecuttemplates.com/contact-us?chat=true" target="_blank" rel="noopener noreferrer">
            {t('quote_builder.missing_integration')}
          </a>
        </p>
      </div>

    </div>
  );
};

export default DataSourcesSettings;
